import { httpApi } from '@app/api/http.api';

/**
 * The client asked for "Excel (.xlsx) or CSV", so every admin report offers both.
 * `xlsx` is a real workbook (typed columns, frozen header, autofilter); `csv` stays
 * for whatever ingests the file rather than reads it.
 */
export type ReportFormat = 'xlsx' | 'csv';

/** `from` / `to` are ISO timestamps; both optional (omitted = no bound). */
export interface ReportFilter {
  from?: string;
  to?: string;
}

export interface ReportExportFile {
  blob: Blob;
  /** From Content-Disposition; null when the header isn't exposed to the browser (CORS). */
  filename: string | null;
  /**
   * The server hit its export row cap, so the file is a prefix of the real result set.
   * Reads as `false` when the header isn't exposed to the browser (CORS).
   */
  truncated: boolean;
}

/** Matches both `filename="x.csv"` and RFC 5987 `filename*=UTF-8''x.csv`. */
const CONTENT_DISPOSITION_FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

/**
 * Downloads a report endpoint in `format`.
 *
 * Fetched as a blob through `httpApi` rather than pointed at with `window.open`, because
 * these endpoints need the Authorization header the request interceptor adds. The response
 * is UTF-8 with a BOM (so Excel reads accented names correctly) and is handed to the caller
 * byte-for-byte, never re-encoded.
 *
 * NOTE both headers read here are response headers on a cross-origin request, so the API
 * must list them in `Access-Control-Expose-Headers` or the browser hides them. Without that
 * the download still works, but falls back to a generated filename and can no longer warn
 * that the file was truncated.
 */
export const downloadReport = (
  path: string,
  params: ReportFilter = {},
  format: ReportFormat = 'xlsx',
): Promise<ReportExportFile> =>
  httpApi.get<Blob>(path, { params: { ...params, format }, responseType: 'blob' }).then((response) => {
    const disposition = response.headers?.['content-disposition'];
    const match = typeof disposition === 'string' ? CONTENT_DISPOSITION_FILENAME.exec(disposition) : null;
    return {
      blob: response.data,
      filename: match ? decodeURIComponent(match[1]) : null,
      truncated: String(response.headers?.['x-report-truncated']) === 'true',
    };
  });

/** Triggers a browser download for a fetched blob. */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
